import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  CContainer, 
  CRow, 
  CCol, 
  CButton, 
  CBadge, 
  CSpinner,
  CModal,
  CModalHeader,
  CModalTitle,
  CModalBody,
  CModalFooter
} from '@coreui/react';
import api from '../../api/axiosInstance';

const ChapterWiseQuestionBank = () => {
  const { section, title } = useParams();
  const navigate = useNavigate();

  const [chaptersData, setChaptersData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Category ID State
  const [categoryId, setCategoryId] = useState(null);

  // Access Control State
  const [hasAccess, setHasAccess] = useState(false);

  // Modal State
  const [modalVisible, setModalVisible] = useState(false);
  const [modalConfig, setModalConfig] = useState({ title: '', message: '', type: 'auth' });

  // LocalStorage keys handle karein
  const authToken = localStorage.getItem('authToken');
  const memberId = localStorage.getItem('memberId') || localStorage.getItem('ID');

  const cleanTitle = title ? title.replace(/-/g, ' ') : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    loadPageData();
  }, [section, title]);

  const loadPageData = async () => {
    try {
      setLoading(true);
      setError(null);

      // 1. Fetch Chapter-Wise Content
      const contentRes = await api.get(
        `/api/content/GetChapterWisePapers?section=${section}&category=${encodeURIComponent(cleanTitle)}`,
        { skipAuthCheck: true }
      );

      if (contentRes.data) {
        setChaptersData(contentRes.data.chapters || contentRes.data || []);
      }

      // 2. Fetch Category ID dynamically
      let catId = null;
      try {
        const catRes = await api.get(`/api/categories/GetBySlug?slug=${title}`, { skipAuthCheck: true });
        if (catRes.data) {
          catId = catRes.data.id || catRes.data.Id || catRes.data.ID;
        }
      } catch (catErr) {
        console.warn("Category ID fetch by slug failed:", catErr);
      }

      if (!catId) {
        try {
          const allCatRes = await api.get('/api/Admin/GetAllCategories', { skipAuthCheck: true });
          const categories = allCatRes.data || [];
          const matchedCategory = categories.find(
            c => (c.slug && c.slug.toLowerCase() === title.toLowerCase()) || 
                 (c.title && c.title.toLowerCase().trim() === cleanTitle.toLowerCase().trim())
          );
          if (matchedCategory) {
            catId = matchedCategory.id || matchedCategory.Id;
          }
        } catch (e) {
          console.error("All categories fetch failed:", e);
        }
      }

      // Save categoryId in State
      setCategoryId(catId);

      // 3. Verify Access for Logged-In Member
      if (authToken && memberId) {
        await verifyUserSubscriptionAccess(catId);
      } else {
        setHasAccess(false);
      }

    } catch (err) {
      console.error("Error fetching page data:", err);
      setError("Failed to load chapter-wise topics.");
    } finally {
      setLoading(false);
    }
  };

  const verifyUserSubscriptionAccess = async (catId) => {
    try {
      // 1. Check activeTiers from LocalStorage for "Sigma"
      const activeTiers = JSON.parse(localStorage.getItem('activeTiers') || '[]');
      const hasSigmaTier = activeTiers.some(
        (tier) => String(tier).trim().toLowerCase() === 'sigma'
      );

      if (hasSigmaTier) {
        setHasAccess(true);
        return;
      }

      // 2. LocalStorage allowedCategoryIds Check
      const storedCategoryIds = JSON.parse(localStorage.getItem('allowedCategoryIds') || '[]');
      if (catId && storedCategoryIds.map(id => id.toString().trim()).includes(catId.toString().trim())) {
        setHasAccess(true);
        return;
      }

      // 3. API Subscriptions Check
      const subRes = await api.get(`/api/members/${memberId}/subscription`);
      const userSubscriptions = Array.isArray(subRes.data) ? subRes.data : [subRes.data];

      let isAllowed = false;
      userSubscriptions.forEach(sub => {
        if (!sub) return;
        if (sub.categoryIds) {
          const allowedCategoryArray = sub.categoryIds.split(',').map(id => id.trim());
          if (catId && allowedCategoryArray.includes(catId.toString().trim())) {
            isAllowed = true;
          }
        }
      });

      setHasAccess(isAllowed);
    } catch (err) {
      console.error("Error verifying subscription access:", err);
      setHasAccess(false);
    }
  };

  const handleTopicClick = (topic) => {
    const topicSlug = topic.title ? topic.title.replace(/\s+/g, '-').toLowerCase() : topic.id;
    const targetUrl = `/${section}/${title}/topic/${topicSlug}`;

    if (!authToken) {
      setModalConfig({
        title: 'Authentication Required',
        message: 'Logging in is required to access the questions for this chapter.',
        type: 'auth'
      });
      setModalVisible(true);
      return;
    }

    if (!hasAccess) {
      setModalConfig({
        title: 'Subscription Plan Required',
        message: 'You need an active plan that covers this category to unlock this content.',
        type: 'upgrade'
      });
      setModalVisible(true);
      return;
    }

    navigate(targetUrl);
  };

  // Fixed Line 193 Issue Here: Using state `categoryId` instead of undefined `catId`
  const handleModalConfirm = () => {
    setModalVisible(false);
    if (modalConfig.type === 'auth') {
      navigate('/login');
    } else {
      navigate(`/plans?categoryId=${categoryId || ''}&categoryName=${encodeURIComponent(cleanTitle)}`);
    }
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading Chapters & Topics...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* TOP HEADER BANNER */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-5" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <span className="text-warning fw-bold text-uppercase d-block mb-1" style={{ fontSize: '13px', letterSpacing: '1px' }}>
            {section?.toUpperCase()} CHAPTERWISE MODULE
          </span>
          <h1 className="fw-bold m-0 mt-1 display-6 text-capitalize" style={{ color: 'yellow' }}>
            {cleanTitle.toUpperCase()} Previous Year Questions
          </h1>
          <p className="text-light opacity-90 m-0 small mt-2">
            Chapterwise & Topicwise Practice Repository
          </p>
          <div className="mt-3">
            <CBadge color="danger" className="px-3 py-2 fs-6 rounded-pill fw-bold">
              {chaptersData.length} Chapters Available
            </CBadge>
          </div>
        </div>
      </div>

      {/* MAIN CONTENT CONTAINER */}
      <CContainer className="pb-5 px-3 px-md-4" style={{ maxWidth: '1200px' }}>
        {chaptersData.length > 0 ? (
          chaptersData.map((chapter, idx) => (
            <div key={chapter.id || idx} className="mb-5 bg-white p-4 rounded-3 border shadow-sm">
              <div className="d-flex align-items-center justify-content-between mb-4 pb-2 border-bottom">
                <div className="d-flex align-items-center gap-2">
                  <div style={{ width: '5px', height: '24px', backgroundColor: '#0f172a', borderRadius: '2px' }}></div>
                  <h4 className="fw-bold text-dark m-0" style={{ fontSize: '1.25rem', letterSpacing: '-0.3px' }}>
                    {chapter.chapterName || chapter.title}
                  </h4>
                </div>
                <span className="badge bg-light text-secondary border fw-normal px-2.5 py-1.5" style={{ fontSize: '0.8rem' }}>
                  {chapter.topics?.length || 0} Topics
                </span>
              </div>

              <CRow className="g-3">
                {(chapter.topics || []).map((topic) => {
                  const isLocked = !hasAccess;

                  return (
                    <CCol lg={4} md={6} sm={12} key={topic.id || topic.title}>
                      <div 
                        className="h-100 rounded-3 overflow-hidden border custom-topic-card d-flex flex-column justify-content-between"
                        style={{ backgroundColor: '#ffffff', cursor: 'pointer', transition: 'all 0.25s ease' }}
                        onClick={() => handleTopicClick(topic)}
                      >
                        <div className="p-3 d-flex align-items-start gap-2">
                          <span className="text-primary mt-1" style={{ fontSize: '1.1rem' }}>📌</span>
                          <span className="fw-semibold text-dark" style={{ fontSize: '0.95rem', lineHeight: '1.45' }}>
                            {topic.title}
                          </span>
                        </div>

                        <div 
                          className="py-2 px-3 text-white fw-bold d-flex align-items-center justify-content-between text-center mt-2"
                          style={{ backgroundColor: isLocked ? '#6c757d' : '#0A2540', fontSize: '0.85rem' }}
                        >
                          <span>
                            {isLocked ? "Unlock Topic (Plan Required)" : "View Questions"}
                          </span>
                          <span>{isLocked ? "🔒" : "🔓"}</span>
                        </div>
                      </div>
                    </CCol>
                  );
                })}
              </CRow>
            </div>
          ))
        ) : (
          <div className="alert alert-warning text-center">No chapters found for this category.</div>
        )}

        {/* MODAL FOR ACCESS NOTIFICATION */}
        <CModal visible={modalVisible} onClose={() => setModalVisible(false)} alignment="center">
          <CModalHeader className="bg-dark text-white">
            <CModalTitle className="fw-bold fs-5">{modalConfig.title}</CModalTitle>
          </CModalHeader>
          <CModalBody className="py-4">
            <p className="fs-6 text-dark m-0">{modalConfig.message}</p>
          </CModalBody>
          <CModalFooter>
            <CButton color="secondary" onClick={() => setModalVisible(false)}>
              Close
            </CButton>
            <CButton color={modalConfig.type === 'upgrade' ? "success" : "primary"} className="text-white fw-bold" onClick={handleModalConfirm}>
              {modalConfig.type === 'upgrade' ? "Upgrade / Buy Plan" : "Login Now"}
            </CButton>
          </CModalFooter>
        </CModal>
      </CContainer>
    </div>
  );
};

export default ChapterWiseQuestionBank;